/**
 * Helpers de filtro de data da Dashboard.
 *
 * O backend (`/api/v1/dashboard/...`) espera datas como query params no formato
 * `YYYY-MM-DD`. Ele interpreta `start_date` como 00:00:00 e `end_date` como
 * 23:59:59 no timezone da aplicação, então basta enviar a data — nunca hora,
 * nunca `DD/MM/YYYY`, nunca timestamp.
 */

export type DashboardPreset = '7d' | 'mes' | 'ano' | 'custom';

/**
 * Granularidade do gráfico de faturamento por atalho: "7 dias" e "Este mês"
 * mostram um ponto por dia, "Este ano" mostra um ponto por mês.
 */
export type DashboardGranularity = 'day' | 'month';

/**
 * Formata em `YYYY-MM-DD` usando os componentes locais da data.
 * `toISOString()` NÃO serve aqui: ele converte para UTC e, para quem está em
 * UTC-3 depois das 21h, "hoje" viraria o dia seguinte.
 */
export function formatDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DATE_PARAM_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Garante que só query strings com data válida cheguem à API. */
export function isValidDateParam(value?: string | null): value is string {
  if (!value || !DATE_PARAM_REGEX.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime());
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Intervalo de datas correspondente a cada atalho da barra de filtros.
 *
 * "Este mês" é o padrão da tela e cobre o mês corrente inteiro, do dia 1º ao
 * último dia — inclusive os dias que ainda não chegaram, que aparecem zerados
 * no gráfico. "Este ano" segue a mesma ideia com os 12 meses do ano corrente.
 *
 * "7 dias" é o único atalho móvel: termina sempre em hoje e inclui o dia de
 * hoje, por isso o recuo é de 6 dias.
 */
export function rangeFromPreset(preset: Exclude<DashboardPreset, 'custom'>): DateRange {
  const today = new Date();

  if (preset === 'ano') {
    const start = new Date(today.getFullYear(), 0, 1);
    const end = new Date(today.getFullYear(), 11, 31);
    return { startDate: formatDateParam(start), endDate: formatDateParam(end) };
  }

  if (preset === 'mes') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    // Dia 0 do mês seguinte é o último dia deste mês, com os 28/29/30/31 certos.
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { startDate: formatDateParam(start), endDate: formatDateParam(end) };
  }

  const start = new Date(today);
  start.setDate(today.getDate() - 6);
  return { startDate: formatDateParam(start), endDate: formatDateParam(today) };
}

/**
 * Granularidade do gráfico de faturamento.
 *
 * Os atalhos "7 dias" e "Este mês" são diários; "Este ano" é mensal. Num
 * intervalo personalizado a escolha depende do tamanho: até 31 dias o gráfico
 * fica legível por dia, acima disso agrupamos por mês. O limite também protege
 * a chamada — o modo diário do `sales-by-month` só devolve os últimos 30 dias.
 */
export function granularityFromPreset(
  preset: DashboardPreset,
  range?: DateRange
): DashboardGranularity {
  if (preset === 'ano') return 'month';
  if (preset === '7d' || preset === 'mes') return 'day';

  if (range && isRangeReady(range.startDate, range.endDate)) {
    return eachDayOfRange(range.startDate, range.endDate).length <= 31
      ? 'day'
      : 'month';
  }

  return 'day';
}

/** Rótulo legível do intervalo, para feedback na tela. */
export function describeRange({ startDate, endDate }: DateRange): string {
  const toBr = (value: string) => {
    if (!isValidDateParam(value)) return value;
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  };
  return `${toBr(startDate)} até ${toBr(endDate)}`;
}

export interface DashboardParams {
  storeId?: number | string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  /** Agrupamento diário do `sales-by-month`; acompanha o intervalo. */
  last30Days?: boolean;
}

/**
 * Monta a query string dos endpoints da dashboard, omitindo o que estiver
 * ausente ou inválido.
 *
 * `last30Days` NÃO substitui mais o intervalo: hoje ele é a única forma de
 * pedir o agrupamento diário ao `sales-by-month`, mas as datas seguem junto
 * para que o recorte seja o do filtro da tela. Enquanto o backend ignorar as
 * datas nesse modo, o recorte continua sendo feito no cliente; quando passar a
 * respeitá-las, a resposta já chega correta sem mexer aqui.
 */
export function buildDashboardQuery({
  storeId,
  startDate,
  endDate,
  limit,
  last30Days,
}: DashboardParams): string {
  const params = new URLSearchParams();

  if (storeId !== undefined && storeId !== null && storeId !== '') {
    params.set('store_id', String(storeId));
  }

  if (isValidDateParam(startDate)) params.set('start_date', startDate);
  if (isValidDateParam(endDate)) params.set('end_date', endDate);

  if (last30Days) params.set('last_30_days', '1');

  if (limit) params.set('limit', String(limit));

  return params.toString();
}

/** O intervalo só pode ir para a API quando as duas pontas são coerentes. */
export function isRangeReady(startDate?: string, endDate?: string): boolean {
  return (
    isValidDateParam(startDate) &&
    isValidDateParam(endDate) &&
    startDate <= endDate
  );
}

/** Lista de todos os dias `YYYY-MM-DD` do intervalo, inclusive nas duas pontas. */
export function eachDayOfRange(startDate: string, endDate: string): string[] {
  if (!isRangeReady(startDate, endDate)) return [];

  const days: string[] = [];
  const cursor = new Date(`${startDate}T00:00:00`);
  const last = new Date(`${endDate}T00:00:00`);

  while (cursor.getTime() <= last.getTime()) {
    days.push(formatDateParam(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

/** Lista dos 12 meses do ano do intervalo, como `YYYY-MM`. */
export function eachMonthOfRange(startDate: string, endDate: string): string[] {
  if (!isRangeReady(startDate, endDate)) return [];

  const months: string[] = [];
  const start = new Date(`${startDate}T00:00:00`);
  const last = new Date(`${endDate}T00:00:00`);
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);

  while (
    cursor.getFullYear() < last.getFullYear() ||
    (cursor.getFullYear() === last.getFullYear() && cursor.getMonth() <= last.getMonth())
  ) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
}
